import { Injectable } from '@angular/core';
import { AlarmLevel } from '../models/channelType';
import { AlarmFlags, ChannelFlags, ElementClasses } from '../models/flags';
import { FlagsService } from './channel-flags.service';

class FlashingButton {
    flashingColor: string;
    buttonElement: any;
    elementId: string;
    currentColor: string | undefined;
}

class FlashingFlag {
    color: string;
    ackColor: any;
    element: any;
    elementId: string;
    currentColor: string | undefined;
    styleProperty?: 'backgroundColor' | 'color';
}

@Injectable({ providedIn: 'root' })
export class ColorAnimationService {
    constructor(private flagsService: FlagsService) {
        setInterval(() => {
            this.flashingButtonsMap.forEach(button => {
                if (!this.disabledElementsSet.has(button.elementId))
                    if (button.currentColor === button.flashingColor) {
                        button.buttonElement.style.backgroundColor = '#9cb2fb';
                        button.currentColor = '#9cb2fb';
                    } else {
                        button.buttonElement.style.backgroundColor = button.flashingColor;
                        button.currentColor = button.flashingColor;
                    }
            });
        }, 500);

        setInterval(() => {
            this.flashingSvgMap.forEach(svg => {
                if (svg.currentColor === svg.ackColor) {
                    svg.element.setAttribute('fill', svg.color);
                    svg.currentColor = svg.color;
                } else {
                    svg.element.setAttribute('fill', svg.ackColor);
                    svg.currentColor = svg.ackColor;
                }
            });
        }, 500);

        setInterval(() => {
            this.flashingControllerMap.forEach(controller => {
                if (!this.disabledElementsSet.has(controller.elementId)) {
                    if (controller.currentColor === controller.ackColor) {
                        controller.element.style[controller.styleProperty] = controller.color;
                        controller.currentColor = controller.color;
                    } else {
                        controller.element.style[controller.styleProperty] = controller.ackColor;
                        controller.currentColor = controller.ackColor;
                    }
                }
            });
        }, 500);
    }

    private flashingButtonsMap: Map<string, FlashingButton> = new Map();
    private flashingSvgMap: Map<string, FlashingFlag> = new Map();
    private flashingControllerMap: Map<string, FlashingFlag> = new Map();

    disabledElementsSet: Set<string> = new Set();

    startFlashingButton(flashingColor, buttonElement, elementId) {
        if (this.flashingButtonsMap.has(elementId)) return;
        const flasgingButton = {
            flashingColor,
            buttonElement,
            elementId,
            currentColor: undefined,
        };
        this.flashingButtonsMap.set(elementId, flasgingButton);
    }

    stopFlashingButton(elementId) {
        if (this.flashingButtonsMap.has(elementId)) {
            this.flashingButtonsMap.delete(elementId);
        }
    }

    startFlashingSvg(color, ackColor, element, elementId) {
        const flashingSvg = {
            color,
            ackColor,
            element,
            elementId,
            currentColor: undefined,
        };
        this.flashingSvgMap.set(elementId, flashingSvg);
    }

    removeFlashingSvg(elementId) {
        const flashingSvg = this.flashingSvgMap.get(elementId);
        if (flashingSvg) {
            flashingSvg.element.setAttribute('fill', flashingSvg.color);
            this.flashingSvgMap.delete(elementId);
        }
    }

    startFlashingController(
        color,
        ackColor,
        element,
        elementId,
        styleProperty: 'backgroundColor' | 'color' = 'backgroundColor'
    ) {
        const flashingController = {
            color,
            ackColor,
            element,
            elementId,
            currentColor: undefined,
            styleProperty,
        };
        this.flashingControllerMap.set(elementId, flashingController);
    }

    removeFlashingController(elementId) {
        const flashingController = this.flashingControllerMap.get(elementId);
        if (flashingController) {
            flashingController.element.style[flashingController.styleProperty] = flashingController.color;
            this.flashingControllerMap.delete(elementId);
        }
    }

    getClassesByFlag(f: ChannelFlags) {
        const alarmLevel = this.flagsService.getLevelByRowId(f.id);
        return {
            'flag-alarm': alarmLevel == AlarmLevel.Alarm && this.aggregateFlags(f.flags.alarm) !== 0,
            'flag-caution': alarmLevel == AlarmLevel.Caution && this.aggregateFlags(f.flags.alarm) !== 0,
            'flag-warning': alarmLevel == AlarmLevel.Warning && this.aggregateFlags(f.flags.alarm) !== 0,
            'flag-cancel':
                (this.aggregateFlags(f.flags.cancelAuto1) |
                    this.aggregateFlags(f.flags.cancelAuto2) |
                    this.aggregateFlags(f.flags.cancelAuto3) |
                    this.aggregateFlags(f.flags.cancelManual)) !==
                0,
            'flag-unack': this.aggregateFlags(f.flags.unAck) !== 0,
        };
    }

    hasFlagStatus(classes: ElementClasses): boolean {
        return (
            classes['flag-alarm'] ||
            classes['flag-cancel'] ||
            classes['flag-caution'] ||
            classes['flag-unack'] ||
            classes['flag-warning']
        );
    }

    private aggregateFlags(flags: AlarmFlags) {
        return flags.under | flags.over | flags.feedbackError | flags.highHigh | flags.high | flags.low | flags.lowlow;
    }
}
